import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { of, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { HowInstallComponent } from '@app/shared/modals/jfse/how-install/how-install.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { JfseIframeModalComponent } from '@app/shared/modals/jfse/jfse-iframe-modal/jfse-iframe-modal.component';
import { ModalFullService } from '@services/modal/modal-full.service';
import { AuthService } from '@services/security';
import BillingSecureEnum from '@app/shared/enums/jfse/billing-secure.enum';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class JFSEService {
    DAEMON_URL = 'http://localhost:8895';
    jfseDeamonInitialised = false;

    set CARD_READER_ID(cardReaderId: string) {
        localStorage.setItem('jfse-card-reader-id', cardReaderId);
    }

    get CARD_READER_ID() {
        return localStorage.getItem('jfse-card-reader-id');
    }

    set OS(os: string) {
        localStorage.setItem('os', os);
    }

    get OS() {
        return localStorage.getItem('os');
    }

    constructor(
        private readonly http: HttpClient,
        private readonly toastr: ToastrService,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        private readonly modalFullService: ModalFullService,
        private readonly authService: AuthService,
    ) {
        this.jfseDeamonInitialised = false;
        if (this.authService.canBill && this.authService.isEnableJfse) {
            this.initDaemonJfse();
        }
    }

    initDaemonJfse() {
        this.http
            .get(`${this.DAEMON_URL}/lecteur/id`)
            .pipe(
                tap((res: any) => {
                    this.CARD_READER_ID = res.id;
                    this.OS = res.operatingSystem.includes('Windows')
                        ? 'WINDOWS'
                        : res.operatingSystem;
                    this.jfseDeamonInitialised = true;
                }),
                catchError(() => {
                    this.jfseDeamonInitialised = true;
                    if (!this.authService.isJfseReady) {
                        this.displayToastr4Install();
                    }
                    return of();
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    syncCps() {
        return this.http.put<any>(`${environment.apiUrl}/jfse/sync-cps`, {
            os: this.OS,
            cardReaderId: this.CARD_READER_ID,
        });
    }

    readVital() {
        return this.http.get<any>(`${environment.apiUrl}/jfse/gui/read-vital`, {
            params: {
                os: this.OS,
                cardReaderId: this.CARD_READER_ID,
            },
        });
    }

    generateJfseId(cpsDatas) {
        return this.http
            .put<any>(`${environment.apiUrl}/jfse/generate-id`, {
                os: this.OS,
                cardReaderId: this.CARD_READER_ID,
                cpsDatas,
            })
            .pipe(
                catchError((err) => {
                    this.toastr.error(
                        this.translate.instant('errors.jfse.NOT_CARD'),
                    );
                    return throwError(err);
                }),
            );
    }

    createBill(
        patientId: string,
        secureType = BillingSecureEnum.SESAM,
        billLignes = [],
    ) {
        return this.http
            .post<any>(`${environment.apiUrl}/jfse/bills/create`, {
                os: this.OS,
                cardReaderId: this.CARD_READER_ID,
                billLignes,
                secureType,
                patientId,
            })
            .pipe(
                catchError((err) => {
                    return throwError(err);
                }),
            );
    }

    transmit() {
        return this.http
            .post<any>(`${environment.apiUrl}/jfse/bills/transmit`, {
                os: this.OS,
                cardReaderId: this.CARD_READER_ID,
            })
            .pipe(
                catchError((err) => {
                    return throwError(err);
                }),
            );
    }

    deleteBill(_id: string) {
        return this.http
            .delete<any>(`${environment.apiUrl}/jfse/bills/${_id}`, {
                body: {
                    os: this.OS,
                    cardReaderId: this.CARD_READER_ID,
                },
            })
            .pipe(
                catchError((err) => {
                    return throwError(err);
                }),
            );
    }

    getCaisseName(codeRegime: string, codeCaisse: string, codeCentre: string) {
        return this.http.post<any>(`${environment.apiUrl}/jfse/validate-amo`, {
            os: this.OS,
            cardReaderId: this.CARD_READER_ID,
            codeRegime,
            codeCaisse,
            codeCentre,
        });
    }

    markAsValidated(ncBillIds, jfseBillId, numBill) {
        return this.http.put(
            `${environment.apiUrl}/jfse/bills/validate/${jfseBillId}`,
            { ncBillIds, numBill },
        );
    }

    displayToastr4Install() {
        this.toastr
            .warning(
                this.translate.instant('errors.jfse.NOT_INSTALLED'),
                null,
                {
                    enableHtml: true,
                    messageClass: 'toastLink',
                    disableTimeOut: true,
                    closeButton: true,
                },
            )
            .onTap.pipe(
                tap(() => this.displayClientHelper()),
                untilDestroyed(this),
            )
            .subscribe();
    }

    displayClientHelper() {
        this.modalService.show(HowInstallComponent, {
            ignoreBackdropClick: false,
        });
    }

    downloadWindowsClient() {
        window.open(environment.jfse.urlkitWindows, '_blank');
    }

    downloadAppleClient() {
        window.open(environment.jfse.urlKitMac, '_blank');
    }

    displaySettings() {
        this.http
            .get<any>(`${environment.apiUrl}/jfse/gui/settings`, {
                params: {
                    cardReaderId: this.CARD_READER_ID,
                    os: this.OS,
                },
            })
            .pipe(
                tap(({ url }) =>
                    this.modalFullService.open(JfseIframeModalComponent, {
                        url,
                    }),
                ),
                catchError((err) => {
                    this.toastr.error(
                        this.translate.instant('errors.jfse.NOT_CARD'),
                    );
                    return of(err);
                }),
            )
            .subscribe();
    }

    displayVersion() {
        this.http
            .get<any>(`${environment.apiUrl}/jfse/gui/version`, {
                params: {
                    cardReaderId: this.CARD_READER_ID,
                    os: this.OS,
                },
            })
            .pipe(
                tap(({ url }) =>
                    this.modalFullService.open(JfseIframeModalComponent, {
                        url,
                    }),
                ),
                catchError((err) => {
                    this.toastr.error(
                        this.translate.instant('errors.jfse.NOT_CARD'),
                    );
                    return of(err);
                }),
            )
            .subscribe();
    }

    displayFse() {
        this.http
            .get<any>(`${environment.apiUrl}/jfse/gui/fse`, {
                params: {
                    cardReaderId: this.CARD_READER_ID,
                    os: this.OS,
                },
            })
            .pipe(
                tap(({ url }) =>
                    this.modalFullService.open(JfseIframeModalComponent, {
                        url,
                    }),
                ),
                catchError((err) => {
                    this.toastr.error(
                        this.translate.instant('errors.jfse.NOT_CARD'),
                    );
                    return of(err);
                }),
            )
            .subscribe();
    }

    displayScor() {
        this.http
            .get<any>(`${environment.apiUrl}/jfse/gui/scor`, {
                params: {
                    cardReaderId: this.CARD_READER_ID,
                    os: this.OS,
                },
            })
            .pipe(
                tap(({ url }) =>
                    this.modalFullService.open(JfseIframeModalComponent, {
                        url,
                    }),
                ),
                catchError((err) => {
                    this.toastr.error(
                        this.translate.instant('errors.jfse.NOT_CARD'),
                    );
                    return of(err);
                }),
            )
            .subscribe();
    }

    displayTpManagment() {
        this.http
            .get<any>(`${environment.apiUrl}/jfse/gui/tp-management`, {
                params: {
                    cardReaderId: this.CARD_READER_ID,
                    os: this.OS,
                },
            })
            .pipe(
                tap(({ url }) =>
                    this.modalFullService.open(JfseIframeModalComponent, {
                        url,
                    }),
                ),
                catchError((err) => {
                    this.toastr.error(
                        this.translate.instant('errors.jfse.NOT_CARD'),
                    );
                    return of(err);
                }),
            )
            .subscribe();
    }

    getApCVNFC(patientId) {
        return this.http.get<any>(`${environment.apiUrl}/jfse/apcv`, {
            params: {
                cardReaderId: this.CARD_READER_ID,
                os: this.OS,
                patientId,
            },
        });
    }

    getApCVQrcode(patientId) {
        return this.http.get<any>(`${environment.apiUrl}/jfse/qrcode/apcv`, {
            params: {
                cardReaderId: this.CARD_READER_ID,
                os: this.OS,
                patientId,
            },
        });
    }

    restoreApCV(patientId) {
        return this.http.get<any>(`${environment.apiUrl}/jfse/restore/apcv`, {
            params: {
                cardReaderId: this.CARD_READER_ID,
                os: this.OS,
                patientId,
            },
        });
    }

    deleteApCV(tokenApCV) {
        this.http
            .delete<any>(`${environment.apiUrl}/jfse/apcv`, {
                body: {
                    cardReaderId: this.CARD_READER_ID,
                    os: this.OS,
                    tokenApCV,
                },
            })
            .subscribe();
    }

    createApCVBill(tokenApCV, dataAPCV) {
        this.http
            .post<any>(`${environment.apiUrl}/jfse/apcv`, {
                cardReaderId: this.CARD_READER_ID,
                os: this.OS,
                tokenApCV,
                dataAPCV,
            })
            .pipe(
                tap(({ url }) =>
                    this.modalFullService.open(JfseIframeModalComponent, {
                        url,
                    }),
                ),
            )
            .subscribe();
    }
}
